import { Camera, Film, Bookmark, Mail, PenTool, Share2, ShoppingBag, ArrowRight, Calendar } from 'lucide-react';
import { formatDistanceToNow, isPast, isThisWeek, parseISO } from 'date-fns';
import type { ContentItem, ContentStatus } from '../../types/content';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';

const CHANNEL_ICONS: Record<string, typeof Camera> = {
  instagram: Camera,
  tiktok: Film,
  pinterest: Bookmark,
  email: Mail,
  blog: PenTool,
  facebook: Share2,
  etsy: ShoppingBag,
};

const TYPE_STYLES: Record<string, string> = {
  social: 'bg-avatar-pink/20 text-avatar-pink',
  email: 'bg-avatar-green/20 text-avatar-green',
  blog: 'bg-avatar-blue/20 text-avatar-blue',
  ad: 'bg-avatar-orange/20 text-avatar-orange',
  video: 'bg-avatar-cyan/20 text-avatar-cyan',
};

const NEXT_STATUS: Record<string, ContentStatus> = {
  idea: 'draft',
  draft: 'review',
  review: 'scheduled',
  scheduled: 'published',
};

interface ContentCardProps {
  item: ContentItem;
  onStatusChange: (id: number, status: ContentStatus) => void;
  onClick: () => void;
}

function getDueDateStyle(dueDate: string | null): string {
  if (!dueDate) return '';
  const date = parseISO(dueDate);
  if (isPast(date)) return 'text-error';
  if (isThisWeek(date)) return 'text-warning';
  return 'text-success';
}

export default function ContentCard({ item, onStatusChange, onClick }: ContentCardProps) {
  const agent = item.assigned_agent ? getAgentBySlug(item.assigned_agent) : null;
  const nextStatus = NEXT_STATUS[item.status];
  const typeStyle = TYPE_STYLES[item.content_type] || TYPE_STYLES.social;
  const ChannelIcon = item.channel ? CHANNEL_ICONS[item.channel] : null;

  function handleAdvance(e: React.MouseEvent) {
    e.stopPropagation();
    if (nextStatus) {
      onStatusChange(item.id, nextStatus);
    }
  }

  return (
    <div
      className="bg-bg-secondary border border-border-subtle rounded-lg p-3 hover:border-border-hover cursor-pointer transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-text-primary text-sm leading-snug">{item.title}</h4>
        <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${typeStyle}`}>
          {item.content_type}
        </span>
      </div>

      {item.channel ? (
        <div className="flex items-center gap-1.5 mb-2">
          {ChannelIcon ? <ChannelIcon className="h-3 w-3 text-text-muted" /> : null}
          <span className="text-xs text-text-muted capitalize">{item.channel}</span>
        </div>
      ) : null}

      {item.due_date ? (
        <div className={`flex items-center gap-1 mb-2 text-xs ${getDueDateStyle(item.due_date)}`}>
          <Calendar className="h-3 w-3" />
          <span>Due {formatDistanceToNow(parseISO(item.due_date), { addSuffix: true })}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          {agent ? (
            <div className="flex items-center gap-1.5">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${getAgentColor(agent.department)}`}>
                {agent.name.charAt(0)}
              </div>
              <span className="text-xs text-text-secondary">{agent.name}</span>
            </div>
          ) : (
            <span className="text-xs text-text-muted">Unassigned</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
          </span>
          {nextStatus ? (
            <button
              onClick={handleAdvance}
              className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-accent-purple-light transition-colors"
              title={`Move to ${nextStatus}`}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
